/** 4 -> 5: a battle hero has a body.
 *
 * `BattleHeroState.wounds` - what the hero has taken swinging. `heroStrike`
 * used to be free damage (measured: the AI took it in 36 battles out of 36),
 * and now the stack it hits hits back, at the hero rather than at his army.
 * Past `heroStamina` he goes down and the fight ends as a retreat.
 *
 * A save written before this recorded no wounds because nothing could inflict
 * one, so the rung writes zero: a hero paused mid-battle resumes unhurt. That
 * is the same gift 0004 made to cooldowns and for the same reason - there is no
 * earlier value to recover, and the alternative is inventing one.
 *
 * Only the two battle heroes are touched. `wounds` is battle-scoped by
 * construction: `heroState` mints it at zero every time a battle starts, so a
 * hero on the adventure map has never carried one and there is nothing to
 * migrate outside `state.battle`.
 */

import type { Migration } from "./types.js";
import { isRecord, readSave, writeSave } from "./tree.js";

export const migration0005: Migration = {
  from: 4,
  to: 5,
  describe: "give each battle hero a wound counter",
  migrate(raw: unknown): Record<string, unknown> | null {
    const save = readSave(raw);
    if (save === null) return null;

    const battle = save.state["battle"];
    if (!isRecord(battle)) return writeSave(save, save.state, 5);

    const heroes = battle["heroes"];
    if (!Array.isArray(heroes)) return writeSave(save, save.state, 5);

    const next = {
      ...battle,
      heroes: heroes.map((h) =>
        isRecord(h) && !Object.prototype.hasOwnProperty.call(h, "wounds")
          ? { ...h, wounds: 0 }
          : h),
    };
    return writeSave(save, { ...save.state, battle: next }, 5);
  },
};
